// API Response Types
export interface ApiResponse<T> {
  data: T;
  message?: string;
  success?: boolean;
}

export interface ListResponse<T> {
  items: T[];
  total: number;
}

// User Types
export enum UserRole {
  MANAGER = 'manager',
  SURVEYOR = 'surveyor',
  CUSTOMER = 'customer',
}

export interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  phone?: string;
  role: UserRole;
  company_name?: string;
  mcs_number?: string;
  is_active: boolean;
  is_verified: boolean;
  full_name: string;
  created_at: string;
  updated_at?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  phone?: string;
  role: UserRole;
  company_name?: string;
  mcs_number?: string;
}

export interface AuthResponse {
  access_token: string;
  token_type: string;
  user: User;
}

// Customer Types
export interface Customer {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  address_line_1: string;
  address_line_2?: string;
  city: string;
  county?: string;
  postcode: string;
  property_type?: string;
  property_age?: string;
  floor_area?: number;
  notes?: string;
  lead_source?: string;
  full_name: string;
  full_address: string;
  created_by: number;
  created_at: string;
  updated_at?: string;
}

export interface CustomerCreate {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  address_line_1: string;
  address_line_2?: string;
  city: string;
  county?: string;
  postcode: string;
  property_type?: string;
  property_age?: string;
  floor_area?: number;
  notes?: string;
  lead_source?: string;
}

// Project Types
export enum ProjectType {
  SOLAR_PV = 'solar_pv',
  HEAT_PUMP = 'heat_pump',
  EV_CHARGING = 'ev_charging',
  BATTERY_STORAGE = 'battery_storage',
  COMBINED = 'combined',
}

export enum ProjectStatus {
  LEAD = 'lead',
  QUALIFIED = 'qualified',
  SURVEY_SCHEDULED = 'survey_scheduled',
  SURVEY_COMPLETED = 'survey_completed',
  QUOTED = 'quoted',
  APPROVED = 'approved',
  INSTALLING = 'installing',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

export interface Project {
  id: number;
  project_number: string;
  title: string;
  description?: string;
  project_type: ProjectType;
  status: ProjectStatus;
  status_display: string;
  project_type_display: string;
  customer_id: number;
  created_by: number;
  assigned_surveyor?: number;
  estimated_cost?: number;
  final_cost?: number;
  deposit_amount?: number;
  deposit_paid: boolean;
  system_size?: string;
  equipment_details?: string;
  mcs_required: boolean;
  mcs_certificate_number?: string;
  notes?: string;
  created_at: string;
  updated_at?: string;
}

export interface ProjectCreate {
  title: string;
  description?: string;
  project_type: ProjectType;
  customer_id: number;
  estimated_cost?: number;
  system_size?: string;
  equipment_details?: string;
  notes?: string;
}

export interface ProjectUpdate {
  title?: string;
  description?: string;
  status?: ProjectStatus;
  estimated_cost?: number;
  final_cost?: number;
  system_size?: string;
  equipment_details?: string;
  notes?: string;
  internal_notes?: string;
  assigned_surveyor?: number;
}

// Document Types
export enum DocumentType {
  PHOTO = 'photo',
  QUOTE = 'quote',
  CONTRACT = 'contract',
  SURVEY_REPORT = 'survey_report',
  MCS_CERTIFICATE = 'mcs_certificate',
  DNO_APPLICATION = 'dno_application',
  DNO_APPROVAL = 'dno_approval',
  INSTALLATION_CERTIFICATE = 'installation_certificate',
  WARRANTY = 'warranty',
  USER_GUIDE = 'user_guide',
  INVOICE = 'invoice',
  OTHER = 'other',
}

export interface Document {
  id: number;
  filename: string;
  original_filename: string;
  title?: string;
  description?: string;
  document_type: DocumentType;
  document_type_display: string;
  file_size?: number;
  file_size_mb: number;
  mime_type?: string;
  is_image: boolean;
  is_pdf: boolean;
  is_customer_visible: boolean;
  project_id: number;
  uploaded_by: number;
  created_at: string;
}

// Survey Types
export enum SurveyStatus {
  SCHEDULED = 'scheduled',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

export interface Survey {
  id: number;
  survey_number: string;
  title: string;
  status: SurveyStatus;
  status_display: string;
  project_id: number;
  surveyor_id: number;
  scheduled_date?: string;
  start_time?: string;
  end_time?: string;
  duration_minutes: number;
  is_completed: boolean;
  notes?: string;
  created_at: string;
  updated_at?: string;
}

export interface SurveyCreate {
  title: string;
  project_id: number;
  surveyor_id: number;
  scheduled_date?: string;
  notes?: string;
}

// Dashboard Types
export interface ProjectStats {
  total_projects: number;
  stats_by_status: Record<string, number>;
  stats_by_type: Record<string, number>;
  recent_projects: Project[];
}

// Form Types
export interface FormError {
  field: string;
  message: string;
}

// API Error Types
export interface ApiError {
  detail: string;
  error_type?: string;
}

// Kanban Types
export interface KanbanColumn {
  id: string;
  title: string;
  status: ProjectStatus;
  projects: Project[];
}

// Filter Types
export interface ProjectFilters {
  status?: ProjectStatus;
  project_type?: ProjectType;
  search?: string;
}

export interface CustomerFilters {
  search?: string;
}

export interface DocumentFilters {
  project_id?: number;
  document_type?: DocumentType;
}

export interface SurveyFilters {
  status?: SurveyStatus;
  project_id?: number;
  surveyor_id?: number;
}

// Inventory Types
export enum InventoryCategory {
  HEAT_PUMP = 'heat_pump',
  CYLINDER = 'cylinder',
  CONTROLS = 'controls',
  SOLAR_PANEL = 'solar_panel',
  BATTERY = 'battery',
  EV_CHARGER = 'ev_charger',
  OTHER = 'other',
}

export const INVENTORY_CATEGORY_LABELS: Record<InventoryCategory, string> = {
  [InventoryCategory.HEAT_PUMP]: 'Heat Pump',
  [InventoryCategory.CYLINDER]: 'Hot Water Cylinder',
  [InventoryCategory.CONTROLS]: 'Controls & Thermostat',
  [InventoryCategory.SOLAR_PANEL]: 'Solar Panel',
  [InventoryCategory.BATTERY]: 'Battery Storage',
  [InventoryCategory.EV_CHARGER]: 'EV Charger',
  [InventoryCategory.OTHER]: 'Other',
};

export interface InventoryItem {
  id: number;
  name: string;
  manufacturer: string;
  model_number?: string;
  category: InventoryCategory;
  category_display: string;
  capacity_kw?: number;
  cop?: number;
  unit_price: number;
  unit_price_inc_vat: number;
  cost_price?: number;
  vat_rate: number;
  in_stock: boolean;
  stock_quantity?: number;
  notes?: string;
  is_active: boolean;
  created_by: number;
  created_at: string;
  updated_at?: string;
}

export interface InventoryCreate {
  name: string;
  manufacturer: string;
  model_number?: string;
  category: InventoryCategory;
  capacity_kw?: number;
  cop?: number;
  unit_price: number;
  cost_price?: number;
  vat_rate: number;
  in_stock: boolean;
  stock_quantity?: number;
  notes?: string;
  is_active: boolean;
}

export interface InventoryUpdate extends Partial<InventoryCreate> {}

export interface InventoryFilters {
  category?: InventoryCategory;
  in_stock_only?: boolean;
  search?: string;
}

// Quote Types
export enum QuoteStatus {
  DRAFT = 'draft',
  SENT = 'sent',
  ACCEPTED = 'accepted',
  REJECTED = 'rejected',
}

export const QUOTE_STATUS_LABELS: Record<QuoteStatus, string> = {
  [QuoteStatus.DRAFT]: 'Draft',
  [QuoteStatus.SENT]: 'Sent',
  [QuoteStatus.ACCEPTED]: 'Accepted',
  [QuoteStatus.REJECTED]: 'Rejected',
};

export const QUOTE_STATUS_COLORS: Record<QuoteStatus, 'default' | 'info' | 'success' | 'error'> = {
  [QuoteStatus.DRAFT]: 'default',
  [QuoteStatus.SENT]: 'info',
  [QuoteStatus.ACCEPTED]: 'success',
  [QuoteStatus.REJECTED]: 'error',
};

export interface QuoteLineItem {
  id: number;
  quote_id: number;
  inventory_item_id?: number;
  description: string;
  quantity: number;
  unit_price: number;
  vat_rate: number;
  line_total: number;
  is_labor: boolean;
  sort_order: number;
}

export interface QuoteLineItemCreate {
  inventory_item_id?: number;
  description: string;
  quantity: number;
  unit_price: number;
  vat_rate: number;
  is_labor: boolean;
  sort_order?: number;
}

export interface Quote {
  id: number;
  quote_number: string;
  project_id: number;
  customer_id: number;
  status: QuoteStatus;
  status_display: string;
  floor_area_m2?: number;
  heat_loss_kw?: number;
  recommended_pump_kw?: number;
  construction_age_band?: string;
  epc_rating?: string;
  epc_retrieved: boolean;
  property_data?: Record<string, any>;
  annual_heat_demand_kwh?: number;
  estimated_annual_saving_gbp?: number;
  estimated_co2_saving_kg?: number;
  labor_cost: number;
  subtotal_ex_vat?: number;
  vat_amount?: number;
  bus_grant: number;
  total_inc_vat?: number;
  total_after_grant?: number;
  line_items: QuoteLineItem[];
  notes?: string;
  valid_until?: string;
  created_by: number;
  created_at: string;
  updated_at?: string;
}

export interface QuoteCreate {
  project_id: number;
  use_epc?: boolean;
  floor_area_m2?: number;
  age_string?: string;
  wall_insulation?: string;
  loft_insulation?: string;
  glazing_type?: string;
  floor_insulated?: boolean;
  labor_cost?: number;
  bus_grant?: number;
  line_items?: QuoteLineItemCreate[];
  notes?: string;
  valid_days?: number;
}

export interface QuoteUpdate {
  status?: QuoteStatus;
  labor_cost?: number;
  bus_grant?: number;
  notes?: string;
  line_items?: QuoteLineItemCreate[];
}

export interface EPCLookupResult {
  found: boolean;
  postcode: string;
  address?: string;
  floor_area_m2?: number;
  construction_age_band?: string;
  epc_rating?: string;
  walls_description?: string;
  roof_description?: string;
  windows_description?: string;
  num_habitable_rooms?: number;
  raw?: Record<string, any>;
}

// Quote Request Types
export enum QuoteRequestStatus {
  PENDING = 'pending',
  IN_REVIEW = 'in_review',
  QUOTED = 'quoted',
  CLOSED = 'closed',
}

export enum QuoteRequestProductInterest {
  HEAT_PUMP = 'heat_pump',
  SOLAR_PV = 'solar_pv',
  BATTERY_STORAGE = 'battery_storage',
  EV_CHARGING = 'ev_charging',
  COMBINED = 'combined',
  OTHER = 'other',
}

export const QUOTE_REQUEST_STATUS_LABELS: Record<QuoteRequestStatus, string> = {
  [QuoteRequestStatus.PENDING]: 'Pending',
  [QuoteRequestStatus.IN_REVIEW]: 'In Review',
  [QuoteRequestStatus.QUOTED]: 'Quoted',
  [QuoteRequestStatus.CLOSED]: 'Closed',
};

export const QUOTE_REQUEST_STATUS_COLORS: Record<QuoteRequestStatus, 'default' | 'warning' | 'info' | 'success' | 'error'> = {
  [QuoteRequestStatus.PENDING]: 'warning',
  [QuoteRequestStatus.IN_REVIEW]: 'info',
  [QuoteRequestStatus.QUOTED]: 'success',
  [QuoteRequestStatus.CLOSED]: 'default',
};

export const PRODUCT_INTEREST_LABELS: Record<QuoteRequestProductInterest, string> = {
  [QuoteRequestProductInterest.HEAT_PUMP]: 'Air Source Heat Pump',
  [QuoteRequestProductInterest.SOLAR_PV]: 'Solar PV',
  [QuoteRequestProductInterest.BATTERY_STORAGE]: 'Battery Storage',
  [QuoteRequestProductInterest.EV_CHARGING]: 'EV Charging',
  [QuoteRequestProductInterest.COMBINED]: 'Combined System',
  [QuoteRequestProductInterest.OTHER]: 'Other',
};

export interface QuoteRequest {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
  phone?: string;
  address_line_1: string;
  city: string;
  postcode: string;
  product_interest: QuoteRequestProductInterest;
  message?: string;
  status: QuoteRequestStatus;
  status_display: string;
  customer_id?: number;
  quote_id?: number;
  created_at: string;
  updated_at?: string;
}

export interface QuoteRequestCreate {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  address_line_1: string;
  city: string;
  postcode: string;
  product_interest: QuoteRequestProductInterest;
  message?: string;
}

// Age band options for manual entry
export const AGE_BAND_OPTIONS = [
  { value: 'pre_1900',  label: 'Pre-1900' },
  { value: '1900_1929', label: '1900–1929' },
  { value: '1930_1949', label: '1930–1949' },
  { value: '1950_1966', label: '1950–1966' },
  { value: '1967_1975', label: '1967–1975' },
  { value: '1976_1982', label: '1976–1982' },
  { value: '1983_1990', label: '1983–1990' },
  { value: '1991_1995', label: '1991–1995' },
  { value: '1996_2002', label: '1996–2002' },
  { value: '2003_2006', label: '2003–2006' },
  { value: '2007_2011', label: '2007–2011' },
  { value: '2012_plus', label: '2012 or later' },
];
